import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";

import ChecklistAccordion from "./ChecklistAccordion";
import { ItemStatus } from "./ChecklistItem";

import { useAddMeetingAgendaItem, useEditMeetingAgendaItem, useMeetingAgendaItems } from "../../../../../api/hooks";
import useToast from "../../../../../hooks/useToast";

import { useMeetingContext } from "../Meetings/MeetingContext";

const AgendaItemAccordion = () => {
	const { meeting, isInteractionDisabled } = useMeetingContext();
	const { data: items, isLoading } = useMeetingAgendaItems(meeting._id);
	const { mutate: addItem } = useAddMeetingAgendaItem();
	const { mutate: editItem } = useEditMeetingAgendaItem();
	const queryClient = useQueryClient();
	const toast = useToast();

	const [isAddingItem, setIsAddingItem] = useState(false);
	const [newItemName, setNewItemName] = useState("");
	const [editingItemId, setEditingItemId] = useState<string | null>(null);
	const [editingItemCurrentValue, setEditingItemCurrentValue] = useState("");
	const [originalItemValue, setOriginalItemValue] = useState("");

	const startEditingItem = (itemId: string, currentValue: string) => {
		// Don't allow editing if interactions are disabled
		if (isInteractionDisabled) return;

		// If we're currently adding a new item with valid text, submit it first
		if (isAddingItem && newItemName.trim()) {
			// Save the current new item text
			const itemToAdd = newItemName;

			// Reset the new item state
			setIsAddingItem(false);
			setNewItemName("");

			// Submit the new item
			handleAddItem(itemToAdd);
		} else if (isAddingItem) {
			// If adding item but no valid text, just dismiss it
			setIsAddingItem(false);
			setNewItemName("");
		}

		// If we're already editing another item, handle the current edit
		if (editingItemId && editingItemId !== itemId) {
			if (editingItemCurrentValue.trim() !== "") {
				// Save the current edit if it's not empty
				handleSaveEdit(editingItemId, editingItemCurrentValue);
			} else {
				// Revert the edit if it's empty - we don't need to call any API
				// Just update the UI to show that we've reverted the empty edit
				const previousItems = items || [];
				const optimisticItems = previousItems.map((item) => {
					if (item._id === editingItemId) {
						return { ...item, name: originalItemValue };
					}
					return item;
				});

				// Apply optimistic update to restore original value
				queryClient.setQueryData(["get-meeting-agenda-items-by-meeting", meeting._id], [optimisticItems]);
			}
		}

		setEditingItemId(itemId);
		setEditingItemCurrentValue(currentValue);
		setOriginalItemValue(currentValue);
	};

	const handleSaveEdit = (itemId: string, newName: string) => {
		const trimmedName = newName.trim();

		// Find the original item to compare with
		const currentItem = items?.find((item) => item._id === itemId);

		// Don't send request if the name hasn't changed
		if (currentItem && currentItem.name === trimmedName) {
			// Just close the edit mode without API request
			return;
		}

		// Create optimistic update
		const previousItems = items || [];
		const optimisticItems = previousItems.map((item) => {
			if (item._id === itemId) {
				return { ...item, name: trimmedName };
			}
			return item;
		});

		// Apply optimistic update
		queryClient.setQueryData(["get-meeting-agenda-items-by-meeting", meeting._id], [optimisticItems]);

		editItem(
			{
				agendaItemId: itemId,
				data: { name: trimmedName },
			},
			{
				onSuccess: () => {
					toast("Agenda item updated", { type: "success", size: "large" });
				},
				onError: () => {
					// Revert to previous state on error
					queryClient.setQueryData(["get-meeting-agenda-items-by-meeting", meeting._id], [previousItems]);
					toast("Failed to update agenda item", { type: "error", size: "large" });
				},
				onSettled: () => {
					// Refetch to ensure data consistency
					queryClient.invalidateQueries({
						queryKey: ["get-meeting-agenda-items-by-meeting", meeting._id],
					});
				},
			}
		);
	};

	const stopEditingItem = (save: boolean) => {
		if (save && editingItemId && editingItemCurrentValue.trim()) {
			handleSaveEdit(editingItemId, editingItemCurrentValue);
		}
		setEditingItemId(null);
		setEditingItemCurrentValue("");
		setOriginalItemValue("");
	};

	const updateEditingItemValue = (value: string) => {
		setEditingItemCurrentValue(value);
	};

	const handleAddItem = (name: string) => {
		// Don't allow adding if interactions are disabled
		if (isInteractionDisabled) return;

		const trimmedName = name.trim();
		const tempId = `temp-${Date.now()}`;

		// Create optimistic item
		const newItem = {
			_id: tempId,
			name: trimmedName,
			meetings: [{ meetingId: meeting._id, status: "incomplete" }],
			created: { userId: "", timeStamp: new Date() },
			updated: { userId: "", timeStamp: new Date() },
		};

		// Apply optimistic update
		const previousItems = items || [];
		queryClient.setQueryData(["get-meeting-agenda-items-by-meeting", meeting._id], [[...previousItems, newItem]]);

		setIsAddingItem(false);
		setNewItemName("");

		addItem(
			{
				data: {
					name: trimmedName,
					meetings: [{ meetingId: meeting._id, status: "incomplete" }],
				},
			},
			{
				onSuccess: () => {
					toast("Agenda item created", { type: "success", size: "large" });
				},
				onError: () => {
					// Revert to previous state on error
					queryClient.setQueryData(["get-meeting-agenda-items-by-meeting", meeting._id], [previousItems]);
					toast("Failed to create agenda item", { type: "error", size: "large" });
				},
				onSettled: () => {
					// Refetch to ensure data consistency
					queryClient.invalidateQueries({
						queryKey: ["get-meeting-agenda-items-by-meeting", meeting._id],
					});
				},
			}
		);
	};

	const handleStatusChange = (itemId: string, status: ItemStatus) => {
		// Don't allow status changes if interactions are disabled
		if (isInteractionDisabled) return;

		// Find the current item to get its meetings array
		const currentItem = items?.find((item) => item._id === itemId);
		if (!currentItem) return;

		// Create optimistic update
		const previousItems = items || [];
		const optimisticItems = previousItems.map((item) => {
			if (item._id === itemId) {
				return {
					...item,
					meetings: item.meetings.map((m) => (m.meetingId === meeting._id ? { ...m, status } : m)),
				};
			}
			return item;
		});

		// Apply optimistic update
		queryClient.setQueryData(["get-meeting-agenda-items-by-meeting", meeting._id], [optimisticItems]);

		// Update the meetings array for the current meeting while preserving others
		const updatedMeetings = currentItem.meetings.map((m) => (m.meetingId === meeting._id ? { ...m, status } : m));

		editItem(
			{
				agendaItemId: itemId,
				data: {
					meetings: updatedMeetings,
				},
			},
			{
				onSuccess: () => {
					toast("Agenda item updated", { type: "success", size: "large" });
				},
				onError: () => {
					// Revert to previous state on error
					queryClient.setQueryData(["get-meeting-agenda-items-by-meeting", meeting._id], [previousItems]);
					toast("Failed to update agenda item", { type: "error", size: "large" });
				},
				onSettled: () => {
					// Refetch to ensure data consistency
					queryClient.invalidateQueries({
						queryKey: ["get-meeting-agenda-items-by-meeting", meeting._id],
					});
				},
			}
		);
	};

	const handleAddButtonClick = () => {
		// Don't allow adding if interactions are disabled
		if (isInteractionDisabled) return;

		// If already adding an item with valid text, submit it first and then start a new one
		if (isAddingItem && newItemName.trim()) {
			// Create a reference to the current item text
			const currentItemName = newItemName;

			// Reset the new item state first, so the add process is clean
			setNewItemName("");

			// Submit the current item
			handleAddItem(currentItemName);

			// Temporarily set isAddingItem to false to reset focus effect
			setIsAddingItem(false);

			// Use setTimeout to ensure the state change is processed before setting it back to true to add a new item
			setTimeout(() => {
				setIsAddingItem(true);
			}, 0);

			return;
		}

		// Normal flow if we're not already adding an item or the current item is empty
		setIsAddingItem(true);
	};

	return (
		<ChecklistAccordion
			title="Meeting Agenda"
			items={items || []}
			isLoading={isLoading}
			defaultExpanded={true}
			isAddingItem={isAddingItem}
			newItemName={newItemName}
			editingItemId={editingItemId}
			editingItemCurrentValue={editingItemCurrentValue}
			setNewItemName={setNewItemName}
			setIsAddingItem={setIsAddingItem}
			startEditingItem={startEditingItem}
			stopEditingItem={stopEditingItem}
			updateEditingItemValue={updateEditingItemValue}
			handleAddItem={handleAddItem}
			handleAddButtonClick={handleAddButtonClick}
			handleStatusChange={handleStatusChange}
			itemType="agenda"
		/>
	);
};

export default AgendaItemAccordion;
